import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import type { Tracking } from '@trivio/contracts';
import { Button } from '@tervior-tech/tervior-shared-ui-react';
import { useDashboardSessionKey } from '../../app/DashboardSessionKeyContext';
import { ApiProblemError, getTracking } from '../../lib/api-client';
import { Card } from '../../components/ui/Card';
import { Input } from '../../components/ui/Input';
import { Label } from '../../components/ui/Label';
import { Alert } from '../../components/ui/Alert';
import { LoadingState } from '../../components/ui/Spinner';
import { TrackingTimeline } from '../labels/TrackingTimeline';

/**
 * `/tracking` (TTP-244 nav item "Rastreio") — a direct lookup by label ID,
 * for the "olhar o rastreio de um envio específico sem passar pela lista"
 * use case (support/triage). Per-label rastreio embedded in
 * `/labels/:id` (`LabelDetailPage.tsx`) covers the "estou olhando essa
 * etiqueta, quero ver o rastreio dela" path; this page covers "tenho um
 * ID/código em mãos, quero ir direto". Reuses `TrackingTimeline` — no
 * separate rendering logic.
 */
export function TrackingPage() {
  const { key } = useDashboardSessionKey();
  const apiKey = key?.key ?? null;

  const [labelIdInput, setLabelIdInput] = useState('');
  const [searchedLabelId, setSearchedLabelId] = useState<string | null>(null);
  const [tracking, setTracking] = useState<Tracking | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const trimmed = labelIdInput.trim();
    if (!apiKey || !trimmed) {
      return;
    }
    setSearchedLabelId(trimmed);
    setTracking(null);
    setError(null);
    setLoading(true);
    getTracking(apiKey, trimmed)
      .then(setTracking)
      .catch((err: unknown) => {
        setError(
          err instanceof ApiProblemError
            ? (err.problem.detail ?? err.problem.title)
            : 'Não foi possível carregar o rastreio agora.',
        );
      })
      .finally(() => setLoading(false));
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-2">
        <h1 className="text-2xl font-medium text-foreground">Rastreio</h1>
        <p className="text-sm text-muted-foreground">
          Busque o rastreio de uma etiqueta específica pelo ID da etiqueta. Para navegar pela lista completa, veja{' '}
          <Link to="/labels" className="font-medium text-primary underline-offset-4 hover:underline">
            Etiquetas
          </Link>
          .
        </p>
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col gap-3 border border-border p-4 sm:flex-row sm:items-end">
        <div className="flex flex-1 flex-col gap-2">
          <Label htmlFor="tracking-label-id">ID da etiqueta</Label>
          <Input
            id="tracking-label-id"
            value={labelIdInput}
            onChange={(event) => setLabelIdInput(event.target.value)}
            placeholder="Ex.: lbl_abc123"
          />
        </div>
        <Button type="submit" variant="default" disabled={!labelIdInput.trim() || loading}>
          {loading ? 'Buscando…' : 'Buscar'}
        </Button>
      </form>

      {error ? (
        <Alert variant="error" title="Não foi possível carregar o rastreio">
          {error}
        </Alert>
      ) : null}

      {loading ? (
        <LoadingState label="Carregando rastreio…" />
      ) : tracking && searchedLabelId ? (
        <Card className="flex flex-col">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border p-5">
            <div>
              <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Código de rastreio</p>
              <p className="text-foreground">{tracking.tracking_code}</p>
            </div>
            <Link
              to={`/labels/${searchedLabelId}`}
              className="text-sm font-medium text-primary underline-offset-4 hover:underline"
            >
              Ver etiqueta completa
            </Link>
          </div>
          <div className="p-5">
            <TrackingTimeline events={tracking.events} />
          </div>
        </Card>
      ) : null}
    </div>
  );
}
